import React, { useState } from 'react'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { Button } from '@resiliatech/birdsong'
import { IntakeFlowStep } from '../../constants'
import { sendStepTransitionEvent } from '../../utils'
import {
  MenuItemWithPadding,
  SectionTitle,
  SelectContainer,
  StyledSelect,
} from './WhatYouDo.styled'
import { ActionContainer, Container } from '../../IntakeFlow.styled'

type Props = {
  onNextStep: (nextStep: IntakeFlowStep) => void
  onBackStep: () => void
  onSetUserRole: (role: UserSurveyRole) => void
  onSetUserPrimaryFunction: (primaryFunction: UserSurveyPrimaryFunction) => void
  userPrimaryFunction?: UserSurveyPrimaryFunction
  userRole?: UserSurveyRole
  userJourneyLength: number
}

export const WhatYouDo = ({
  onNextStep,
  onBackStep,
  onSetUserRole,
  onSetUserPrimaryFunction,
  userPrimaryFunction: initialUserPrimaryFunction,
  userRole: initialUserRole,
  userJourneyLength,
}: Props) => {
  const { nonprofitOrgConfig, userConfig } = useAppState()
  const [localUserRole, setLocalUserRole] = useState<string>(
    initialUserRole || ''
  )
  const [localUserPrimaryFunction, setLocalUserPrimaryFunction] =
    useState<string>(initialUserPrimaryFunction || '')
  const [isRoleSelectOpen, setIsRoleSelectOpen] = useState(false)
  const [isFunctionSelectOpen, setIsFunctionSelectOpen] = useState(false)

  const handleRoleChange = (event: React.SyntheticEvent) => {
    const eventTarget = event.target as HTMLInputElement
    const newRole = eventTarget.value as UserSurveyRole
    setLocalUserRole(newRole)
    // Update parent component's local state, but don't persist to DB yet
    onSetUserRole(newRole)
  }

  const handleFunctionChange = (event: React.SyntheticEvent) => {
    const eventTarget = event.target as HTMLInputElement
    const newFunction = eventTarget.value as UserSurveyPrimaryFunction
    setLocalUserPrimaryFunction(newFunction)
    // Update parent component's local state, but don't persist to DB yet
    onSetUserPrimaryFunction(newFunction)
  }

  const isNextDisabled = !localUserRole || !localUserPrimaryFunction

  const handleNext = () => {
    if (!isNextDisabled) {
      sendStepTransitionEvent({
        currentStep: IntakeFlowStep.WHAT_YOU_DO,
        nextStep: IntakeFlowStep.ORG_SUMMARY,
        userId: userConfig.id,
        nonprofitOrgId: nonprofitOrgConfig.orgId,
        funderOrgId: nonprofitOrgConfig.funderOrgId,
        userEmail: userConfig.email,
        userPrimaryFunction:
          localUserPrimaryFunction as UserSurveyPrimaryFunction,
        userJourneyLength,
        userRole: localUserRole as UserSurveyRole,
      })

      // Navigate to next step - this will trigger the parent component to save data
      onNextStep(IntakeFlowStep.ORG_SUMMARY)
    }
  }

  return (
    <Container>
      <div>
        <SectionTitle>What is your primary role?</SectionTitle>
        <SelectContainer size='small'>
          <StyledSelect
            value={localUserRole}
            onChange={(event) =>
              handleRoleChange(event as unknown as React.SyntheticEvent)
            }
            onClose={() => setIsRoleSelectOpen(false)}
            onOpen={() => setIsRoleSelectOpen(true)}
            open={isRoleSelectOpen}
            size='small'
            displayEmpty
            placeholder='Choose your primary role'
          >
            <MenuItemWithPadding disabled value=''>
              Choose your primary role
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.Executive}>
              Executive (CEO, Executive Director, VP, Founder, etc)
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.Director}>
              Director
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.Manager}>
              Manager
            </MenuItemWithPadding>
            <MenuItemWithPadding
              value={UserSurveyRole.AssistantAssociateCoordinator}
            >
              Assistant / Associate / Coordinator
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.Volunteer}>
              Volunteer
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.BoardMember}>
              Board Member
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.TeamMember}>
              Other
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyRole.OrgLeader}>
              Organization Leader
            </MenuItemWithPadding>
          </StyledSelect>
        </SelectContainer>
      </div>

      <div>
        <SectionTitle>What is your primary function?</SectionTitle>
        <SelectContainer size='small'>
          <StyledSelect
            value={localUserPrimaryFunction}
            onChange={(event) =>
              handleFunctionChange(event as unknown as React.SyntheticEvent)
            }
            onClose={() => setIsFunctionSelectOpen(false)}
            onOpen={() => setIsFunctionSelectOpen(true)}
            open={isFunctionSelectOpen}
            size='small'
            displayEmpty
            placeholder='Choose your primary function'
          >
            <MenuItemWithPadding disabled value=''>
              Choose your primary function
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyPrimaryFunction.Executive}>
              Executive
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyPrimaryFunction.Programming}>
              Programming
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyPrimaryFunction.Fundraising}>
              Fundraising
            </MenuItemWithPadding>
            <MenuItemWithPadding
              value={UserSurveyPrimaryFunction.FinanceOperations}
            >
              Finance & Operations
            </MenuItemWithPadding>
            <MenuItemWithPadding
              value={UserSurveyPrimaryFunction.MarketingCommunications}
            >
              Marketing & Communications
            </MenuItemWithPadding>
            <MenuItemWithPadding
              value={UserSurveyPrimaryFunction.MultipleFunctions}
            >
              Multiple Functions
            </MenuItemWithPadding>
            <MenuItemWithPadding value={UserSurveyPrimaryFunction.Other}>
              Other
            </MenuItemWithPadding>
          </StyledSelect>
        </SelectContainer>
      </div>

      <ActionContainer>
        <Button variant='text' onClick={() => onBackStep()}>
          Back
        </Button>
        <Button
          variant='primary'
          onClick={handleNext}
          isDisabled={isNextDisabled}
          label='Next'
        />
      </ActionContainer>
    </Container>
  )
}
